import React from "react";

import { useAuth } from "@/context/auth";
import {
  useCompleteOnboarding,
  useOnboardingDraft,
  useSaveOnboarding,
  useSaveOnboardingExperience,
} from "@/queries/api/onboardingApi";
import { useMyInviteExperienceQuery } from "@/queries/api/workerInviteApi";
import type {
  OnboardingCompletionResponse,
  OnboardingProfileRequest,
} from "@/schemas/onboarding";
import {
  emptyWorkExperience,
  levelForDuration,
} from "@/constants/onboarding";
import type {
  WorkExperience,
  WorkerOnboardingData,
} from "@/constants/onboarding";

function initialData(): WorkerOnboardingData {
  // Seed one empty card so the form is visible from the first paint without
  // depending on a step-level useEffect.
  return {
    name: "",
    email: "",
    workExperiences: [emptyWorkExperience()],
    noExperience: false,
    city: "",
    radius: 15,
  };
}

type WorkerOnboardingContextValue = {
  data: WorkerOnboardingData;
  patch: (p: Partial<WorkerOnboardingData>) => void;
  addWorkExperience: () => void;
  removeWorkExperience: (index: number) => void;
  setWorkExperienceField: (
    index: number,
    field: keyof Omit<WorkExperience, "roles">,
    value: string | boolean,
  ) => void;
  toggleWorkExperienceRole: (index: number, roleKey: string) => void;
  setNoExperience: (value: boolean) => void;
  toRequest: () => OnboardingProfileRequest;
  saveDraft: () => Promise<void>;
  complete: () => Promise<OnboardingCompletionResponse>;
  setPhotoUrl: (url: string | undefined) => void;
  setResumeUrl: (url: string | undefined) => void;
  /** Restaurant whose invite pre-filled the Experience step, if any. */
  invitedBy: string | null;
  isLoading: boolean;
  isSaving: boolean;
};

const Ctx = React.createContext<WorkerOnboardingContextValue | undefined>(
  undefined,
);

export function useWorkerOnboarding() {
  const ctx = React.useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useWorkerOnboarding must be used within WorkerOnboardingProvider",
    );
  }
  return ctx;
}

export function WorkerOnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [data, setData] = React.useState<WorkerOnboardingData>(initialData);
  const auth = useAuth();
  const isEditMode = Boolean(auth.user?.onboardingCompleted);
  const draftQuery = useOnboardingDraft();
  const saveMutation = useSaveOnboarding();
  const saveExperienceMutation = useSaveOnboardingExperience();
  const completeMutation = useCompleteOnboarding();
  // Workers who signed up through a restaurant's invite start with the
  // experience that restaurant entered (restaurant, roles, dates).
  const inviteQuery = useMyInviteExperienceQuery(!isEditMode);
  const [invitedBy, setInvitedBy] = React.useState<string | null>(null);
  const inviteSeededRef = React.useRef(false);

  React.useEffect(() => {
    const draft = draftQuery.data;
    if (!draft) return;
    // Prefer the new roleExperiences key; fall back to the legacy roleInterests
    // key so old draft data (saved before the BE migration) still loads.
    const source = draft.roleExperiences ?? draft.roleInterests;
    // BE stores one experience row per (restaurant, role). Regroup by the
    // (restaurant, startMonth, endMonth, current) signature so the FE can
    // render one card per real job.
    const groups = new Map<string, WorkExperience>();
    for (const r of source) {
      if (!r.restaurant || !r.startMonth) continue; // skip stale "no experience" rows
      const key = `${r.restaurant}|${r.startMonth}|${r.endMonth ?? ""}|${r.current ? "1" : "0"}`;
      const existing = groups.get(key);
      if (existing) {
        if (!existing.roles.includes(r.roleKey)) existing.roles.push(r.roleKey);
      } else {
        groups.set(key, {
          restaurant: r.restaurant,
          start: r.startMonth,
          end: r.endMonth ?? "",
          current: r.current,
          roles: [r.roleKey],
        });
      }
    }
    const loaded = Array.from(groups.values());
    setData((prev) => ({
      ...prev,
      name: draft.fullName ?? prev.name,
      email: draft.email ?? prev.email,
      city: draft.location?.city ?? prev.city,
      radius: draft.location?.radiusMiles ?? prev.radius,
      latitude: draft.location?.latitude ?? prev.latitude,
      longitude: draft.location?.longitude ?? prev.longitude,
      // Keep the seeded empty card if the draft has nothing yet — otherwise the
      // form would flash empty on first load for fresh users.
      workExperiences: loaded.length > 0 ? loaded : prev.workExperiences,
      photoUrl: draft.photoUrl ?? prev.photoUrl,
      resumeUrl: draft.resumeUrl ?? prev.resumeUrl,
    }));
  }, [draftQuery.data]);

  React.useEffect(() => {
    const draft = draftQuery.data;
    const invite = inviteQuery.data;
    if (!draft || !invite || inviteSeededRef.current) return;
    inviteSeededRef.current = true;
    // Only seed a fresh draft; never overwrite experience the worker saved.
    const source = draft.roleExperiences ?? draft.roleInterests;
    if (source.some((r) => r.restaurant && r.startMonth)) return;
    const restaurant = invite.restaurantName?.trim() ?? "";
    if (!restaurant) return;
    setData((prev) => ({
      ...prev,
      noExperience: false,
      workExperiences: [
        {
          restaurant,
          start: invite.workedFrom ?? "",
          end: invite.currentlyWorksHere ? "" : (invite.workedTo ?? ""),
          current: invite.currentlyWorksHere,
          roles: invite.roles,
        },
      ],
    }));
    setInvitedBy(restaurant);
  }, [draftQuery.data, inviteQuery.data]);

  const patch = React.useCallback(
    (p: Partial<WorkerOnboardingData>) => setData((d) => ({ ...d, ...p })),
    [],
  );

  const addWorkExperience = React.useCallback(() => {
    setData((d) => ({
      ...d,
      workExperiences: [...d.workExperiences, emptyWorkExperience()],
    }));
  }, []);

  const removeWorkExperience = React.useCallback((index: number) => {
    setData((d) => ({
      ...d,
      workExperiences: d.workExperiences.filter((_, i) => i !== index),
    }));
  }, []);

  const setWorkExperienceField = React.useCallback(
    (
      index: number,
      field: keyof Omit<WorkExperience, "roles">,
      value: string | boolean,
    ) => {
      setData((d) => {
        const next = d.workExperiences.map((we, i) => {
          if (i !== index) return we;
          const updated: WorkExperience = { ...we, [field]: value };
          if (field === "current" && value === true) updated.end = "";
          return updated;
        });
        return { ...d, workExperiences: next };
      });
    },
    [],
  );

  const toggleWorkExperienceRole = React.useCallback(
    (index: number, roleKey: string) => {
      setData((d) => {
        const next = d.workExperiences.map((we, i) => {
          if (i !== index) return we;
          const has = we.roles.includes(roleKey);
          return {
            ...we,
            roles: has
              ? we.roles.filter((r) => r !== roleKey)
              : [...we.roles, roleKey],
          };
        });
        return { ...d, workExperiences: next };
      });
    },
    [],
  );

  const setNoExperience = React.useCallback((value: boolean) => {
    setData((d) => ({
      ...d,
      noExperience: value,
      // Clear any drafted entries when opting out; reseed a fresh card when
      // opting back in so the form is immediately editable.
      workExperiences: value ? [] : [emptyWorkExperience()],
    }));
  }, []);

  const toRequest = React.useCallback(
    (): OnboardingProfileRequest => {
      // Flatten restaurant × roles into BE's per-role rows. Level is required
      // by the schema; bucket it from the date range so the wire payload stays
      // lossless. An empty array is valid — it means "New to Restaurants".
      const roleExperiences = data.workExperiences.flatMap((we) => {
        const restaurant = we.restaurant.trim();
        if (!restaurant || !we.start || we.roles.length === 0) return [];
        return we.roles.map((roleKey) => ({
          roleKey,
          level: levelForDuration(we.start, we.end, we.current),
          restaurant,
          startMonth: we.start,
          endMonth: we.current ? undefined : we.end || undefined,
          current: we.current,
        }));
      });
      return {
        fullName: data.name.trim() || undefined,
        location: data.city.trim()
          ? {
              city: data.city.trim(),
              radiusMiles: data.radius,
              openToRelocate: false,
              latitude: data.latitude,
              longitude: data.longitude,
            }
          : undefined,
        roleExperiences,
        // Keep roleInterests empty — the new roleExperiences field supersedes it.
        roleInterests: [],
      };
    },
    [data],
  );

  const saveDraft = React.useCallback(async () => {
    const req = toRequest();
    if (isEditMode) {
      // Settings/profile edit path: only update the experience endpoint so we
      // don't accidentally overwrite name/location changes made in other panels.
      await saveExperienceMutation.mutateAsync({
        roleExperiences: req.roleExperiences ?? [],
      });
    } else {
      await saveMutation.mutateAsync(req);
    }
  }, [isEditMode, saveExperienceMutation, saveMutation, toRequest]);

  const complete = React.useCallback(async () => {
    return await completeMutation.mutateAsync();
  }, [completeMutation]);

  const setPhotoUrl = React.useCallback((url: string | undefined) => {
    setData((d) => ({ ...d, photoUrl: url }));
  }, []);

  const setResumeUrl = React.useCallback((url: string | undefined) => {
    setData((d) => ({ ...d, resumeUrl: url }));
  }, []);

  const value: WorkerOnboardingContextValue = React.useMemo(
    () => ({
      data,
      patch,
      addWorkExperience,
      removeWorkExperience,
      setWorkExperienceField,
      toggleWorkExperienceRole,
      setNoExperience,
      toRequest,
      saveDraft,
      complete,
      setPhotoUrl,
      setResumeUrl,
      invitedBy,
      isLoading: draftQuery.isLoading,
      isSaving: saveMutation.isPending || saveExperienceMutation.isPending,
    }),
    [
      data,
      patch,
      addWorkExperience,
      removeWorkExperience,
      setWorkExperienceField,
      toggleWorkExperienceRole,
      setNoExperience,
      toRequest,
      saveDraft,
      complete,
      setPhotoUrl,
      setResumeUrl,
      invitedBy,
      draftQuery.isLoading,
      saveMutation.isPending,
      saveExperienceMutation.isPending,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
